import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createTenantStorage } from './tenantStorage';
import { useMemo } from 'react';
import { type ShellType } from '../types/terminal.types';

export type { ShellType };
export const AVAILABLE_TERMINAL_SHELLS: ShellType[] = [
  'zsh',
  'bash',
  'sh',
  'powershell',
  'cmd',
];

let terminalInstanceKeyCounter = 0;

function createTerminalInstanceKey(sessionId: string): string {
  terminalInstanceKeyCounter += 1;

  return `${sessionId}:${Date.now()}:${terminalInstanceKeyCounter}`;
}

export function getShellOptionsForPlatform({
  platform,
}: {
  platform: string | undefined;
}): ShellType[] {
  if (platform === 'win32' || platform === 'windows') {
    return ['powershell', 'cmd', 'bash'];
  }

  return ['zsh', 'bash', 'sh', 'powershell'];
}

export type TerminalTab = {
  id: string;
  title: string;
  sessionId: string;
  instanceKey?: string;
  cwd?: string;
  isActive: boolean;
  shell?: ShellType;
  ptyId?: string;
};

export type TerminalTabTarget = {
  toolCallId?: string;
  ptyId?: string;
};

export function findTerminalTabForTarget({
  terminalTabs,
  toolCallTabIds,
  target,
}: {
  terminalTabs: TerminalTab[];
  toolCallTabIds: Record<string, string>;
  target: TerminalTabTarget;
}): TerminalTab | undefined {
  const linkedTabId = target.toolCallId
    ? toolCallTabIds[target.toolCallId]
    : undefined;

  return (
    terminalTabs.find((tab) => target.ptyId && tab.ptyId === target.ptyId) ??
    terminalTabs.find((tab) => linkedTabId && tab.id === linkedTabId)
  );
}

export function agentSessionTabId(ptyId: string): string {
  return `agent-session:${ptyId}`;
}

export function persistableTerminalTabs({
  terminalTabs,
  activeTerminalTabId,
}: {
  terminalTabs: TerminalTab[];
  activeTerminalTabId: string | null;
}): { terminalTabs: TerminalTab[]; activeTerminalTabId: string | null } {
  const shellTabs = terminalTabs.filter((tab) => !tab.ptyId);
  const nextActiveTabId = shellTabs.some(
    (tab) => tab.id === activeTerminalTabId,
  )
    ? activeTerminalTabId
    : (shellTabs.at(-1)?.id ?? null);

  return {
    terminalTabs: shellTabs.map((tab) => ({
      ...tab,
      isActive: tab.id === nextActiveTabId,
    })),
    activeTerminalTabId: nextActiveTabId,
  };
}

type TerminalStoreState = {
  terminalTabs: TerminalTab[];
  activeTerminalTabId: string | null;
  readySessionIds: Record<string, boolean>;
  runningCommandSessionIds: Record<string, number>;
  toolCallTabIds: Record<string, string>;
  defaultShell: ShellType;
  focusRequestId: number;

  linkToolCallToTab: (toolCallId: string, tabId: string) => void;
  addAgentSessionTab: (params: { ptyId: string; title: string }) => TerminalTab;
  markTerminalCommandRunning: (sessionId: string) => void;
  markTerminalCommandFinished: (sessionId: string) => void;
  addTerminalTab: (tab: Omit<TerminalTab, 'isActive'>) => void;
  removeTerminalTab: (tabId: string) => void;
  setActiveTerminalTab: (tabId: string) => void;
  setDefaultShell: (shell: ShellType) => void;
  setTerminalReady: (sessionId: string, isReady: boolean) => void;
  isTerminalReady: (sessionId: string) => boolean;
  requestTerminalFocus: () => void;
  refreshTerminalSession: (
    sessionId: string,
    options?: { title?: string; cwd?: string; shell?: ShellType },
  ) => void;
  getOrCreateTerminalTab: (
    sessionId: string,
    options?: { title?: string; cwd?: string; shell?: ShellType },
  ) => TerminalTab;
  clearAllTerminals: () => void;
};

export const useTerminalStore = create<TerminalStoreState>()(
  persist(
    (set, get) => ({
      terminalTabs: [],
      activeTerminalTabId: null,
      readySessionIds: {},
      runningCommandSessionIds: {},
      toolCallTabIds: {},
      defaultShell: 'zsh',
      focusRequestId: 0,

      linkToolCallToTab: (toolCallId, tabId) => {
        set({
          toolCallTabIds: { ...get().toolCallTabIds, [toolCallId]: tabId },
        });
      },

      addAgentSessionTab: ({ ptyId, title }) => {
        const id = agentSessionTabId(ptyId);
        const existingTab = get().terminalTabs.find((tab) => tab.id === id);

        if (existingTab) {
          return existingTab;
        }

        const tab = { id, title, sessionId: id, ptyId };

        get().addTerminalTab(tab);

        return { ...tab, isActive: true };
      },

      markTerminalCommandRunning: (sessionId) => {
        const current = get().runningCommandSessionIds;

        set({
          runningCommandSessionIds: {
            ...current,
            [sessionId]: (current[sessionId] ?? 0) + 1,
          },
        });
      },

      markTerminalCommandFinished: (sessionId) => {
        const current = get().runningCommandSessionIds;
        const next = (current[sessionId] ?? 0) - 1;
        const updated = { ...current };

        if (next > 0) {
          updated[sessionId] = next;
        } else {
          delete updated[sessionId];
        }

        set({ runningCommandSessionIds: updated });
      },

      addTerminalTab: (tab) => {
        const { terminalTabs } = get();
        const updatedTabs = terminalTabs.map((t) => ({
          ...t,
          isActive: false,
        }));
        const newTab: TerminalTab = { ...tab, isActive: true };

        set({
          terminalTabs: [...updatedTabs, newTab],
          activeTerminalTabId: tab.id,
        });
      },

      removeTerminalTab: (tabId) => {
        const { terminalTabs, activeTerminalTabId, runningCommandSessionIds } =
          get();
        const removed = terminalTabs.find((tab) => tab.id === tabId);
        const filteredTabs = terminalTabs.filter((tab) => tab.id !== tabId);
        const nextRunningCommandSessionIds = { ...runningCommandSessionIds };

        delete nextRunningCommandSessionIds[tabId];

        if (removed) {
          delete nextRunningCommandSessionIds[removed.sessionId];
        }

        const updates: Partial<TerminalStoreState> = {
          terminalTabs: filteredTabs,
          runningCommandSessionIds: nextRunningCommandSessionIds,
        };

        if (activeTerminalTabId === tabId && filteredTabs.length > 0) {
          const newActiveTab = filteredTabs[filteredTabs.length - 1];

          updates.activeTerminalTabId = newActiveTab.id;
          updates.terminalTabs = filteredTabs.map((t) => ({
            ...t,
            isActive: t.id === newActiveTab.id,
          }));
        } else if (filteredTabs.length === 0) {
          updates.activeTerminalTabId = null;
        }

        set(updates);
      },

      setActiveTerminalTab: (tabId) => {
        const { terminalTabs } = get();

        set({
          terminalTabs: terminalTabs.map((t) => ({
            ...t,
            isActive: t.id === tabId,
          })),
          activeTerminalTabId: tabId,
        });
      },

      setDefaultShell: (shell) => {
        set({ defaultShell: shell });
      },

      setTerminalReady: (sessionId, isReady) => {
        const { readySessionIds } = get();

        if (isReady) {
          set({
            readySessionIds: {
              ...readySessionIds,
              [sessionId]: true,
            },
          });

          return;
        }

        const nextReadySessionIds = { ...readySessionIds };

        delete nextReadySessionIds[sessionId];
        set({ readySessionIds: nextReadySessionIds });
      },

      isTerminalReady: (sessionId) => {
        return get().readySessionIds[sessionId] === true;
      },

      requestTerminalFocus: () => {
        set({ focusRequestId: get().focusRequestId + 1 });
      },

      refreshTerminalSession: (sessionId, options) => {
        const { terminalTabs, readySessionIds } = get();
        const nextReadySessionIds = { ...readySessionIds };

        delete nextReadySessionIds[sessionId];

        set({
          readySessionIds: nextReadySessionIds,
          terminalTabs: terminalTabs.map((tab) =>
            tab.sessionId === sessionId
              ? {
                  ...tab,
                  title: options?.title ?? tab.title,
                  cwd: options?.cwd ?? tab.cwd,
                  shell: options?.shell ?? tab.shell,
                  instanceKey: createTerminalInstanceKey(sessionId),
                }
              : tab,
          ),
        });
      },

      getOrCreateTerminalTab: (sessionId, options) => {
        const { terminalTabs, addTerminalTab } = get();
        const existingTab = terminalTabs.find((t) => t.sessionId === sessionId);

        if (existingTab) {
          return existingTab;
        }

        const newTab: TerminalTab = {
          id: sessionId,
          title: options?.shell || options?.title || 'zsh',
          sessionId,
          instanceKey: createTerminalInstanceKey(sessionId),
          cwd: options?.cwd,
          shell: options?.shell || 'zsh',
          isActive: true,
        };

        addTerminalTab(newTab);

        return newTab;
      },

      clearAllTerminals: () => {
        set({
          terminalTabs: [],
          activeTerminalTabId: null,
          readySessionIds: {},
          runningCommandSessionIds: {},
          toolCallTabIds: {},
        });
      },
    }),
    {
      name: 'terra-terminal-store',
      storage: createJSONStorage(() => createTenantStorage()),
      partialize: (state) => ({
        ...persistableTerminalTabs(state),
        defaultShell: state.defaultShell,
      }),
    },
  ),
);

// Hook for terminal tabs with derived state
export function useTerminalTabs() {
  const terminalTabs = useTerminalStore((state) => state.terminalTabs);
  const activeTerminalTabId = useTerminalStore(
    (state) => state.activeTerminalTabId,
  );
  const addTerminalTab = useTerminalStore((state) => state.addTerminalTab);
  const removeTerminalTab = useTerminalStore(
    (state) => state.removeTerminalTab,
  );
  const setActiveTerminalTab = useTerminalStore(
    (state) => state.setActiveTerminalTab,
  );
  const getOrCreateTerminalTab = useTerminalStore(
    (state) => state.getOrCreateTerminalTab,
  );
  const refreshTerminalSession = useTerminalStore(
    (state) => state.refreshTerminalSession,
  );
  const defaultShell = useTerminalStore((state) => state.defaultShell);
  const setDefaultShell = useTerminalStore((state) => state.setDefaultShell);

  const activeTerminalTab = useMemo(
    () => terminalTabs.find((tab) => tab.id === activeTerminalTabId),
    [terminalTabs, activeTerminalTabId],
  );

  return {
    terminalTabs,
    activeTerminalTab,
    addTerminalTab,
    removeTerminalTab,
    setActiveTerminalTab,
    getOrCreateTerminalTab,
    refreshTerminalSession,
    defaultShell,
    setDefaultShell,
  };
}
